import { useLocation } from "react-router-dom";
import type { PopulationFilter } from "@digital-twins/shared-types";
import type { PopulationFilter as EditorPopulationFilter } from "@/types/routes/test-results";
import { isStudyTypeClosed } from "@/hooks/useClosedStudyTypes";
import { getGoalById } from "./components/goals/goalsConfig";

/** The existing filter editor represents scalar values as strings. */
export function studyHandoffEditorFilter(filter: PopulationFilter): EditorPopulationFilter {
  return {
    logic: filter.logic ?? "AND",
    conditions: (filter.conditions ?? []).map((condition) => "field" in condition
      ? {
        ...condition,
        operator: condition.operator ?? "equals",
        value: Array.isArray(condition.value) ? condition.value : String(condition.value ?? ""),
      }
      : studyHandoffEditorFilter(condition)),
  };
}

/**
 * The Home brief's hand-off to `/tests/new` (C10#14).
 *
 * The brief travels as router state, the same contract the chat and thread
 * hand-offs already use (`ChatPage` reads `initialMessage` off location
 * state), never as a URL query: a query lands in browser history and in the
 * pageview tracker's `pathname?search`. This module owns the state's shape:
 * `studyBriefHandoffState` is its only writer and `useStudyBriefHandoff` its
 * only reader.
 *
 * The state lives on the history entry Home pushed. The builder's
 * `router.replace('?draftId=')` drops it, which is intended: by then the brief
 * is the draft's first chat turn and the draft id is what a reload reopens.
 */
export interface StudyBriefHandoffState {
  initialMessage: string;
  sourceThreadToken?: string;
  audienceToken?: string;
  populationFilter?: PopulationFilter | null;
  segmentLabel?: string | null;
  goalId?: string | null;
}

/** A suggestion goal reaches the creator only when it is a known, open study type. */
export function handoffGoalSeed(
  goalId: string | null | undefined,
  closed: ReadonlySet<string>,
): string | null {
  if (!goalId || !getGoalById(goalId) || isStudyTypeClosed(closed, goalId)) {
    return null;
  }
  return goalId;
}

/** Typed creator context stays in router state until the draft owns it. */
export function useStudyCreationHandoff(): Partial<StudyBriefHandoffState> | null {
  return useLocation().state as Partial<StudyBriefHandoffState> | null;
}

/** The router state for a brief, or none when the brief is blank. */
export function studyBriefHandoffState(
  message: string,
): StudyBriefHandoffState | undefined {
  const initialMessage = message.trim();
  return initialMessage ? { initialMessage } : undefined;
}

/** The brief Home handed to this page, or null when it handed none. */
export function useStudyBriefHandoff(): string | null {
  const state = useStudyCreationHandoff();
  const brief = state?.initialMessage;
  return typeof brief === "string" && brief.trim() ? brief : null;
}
